<script lang="ts">
import { Component, Vue, Prop, Mixins } from "vue-property-decorator";
import { ICustomerDTO, IUserDTO } from "@shared/types";
import BaseFormDialog from "@/components/global/BaseFormDialog.vue";
import CompanyInfoField from "@/components/global/CompanyInfoField.vue";
import Textfield from "@/components/global/Textfield.vue";
import { customersModule } from "@/store/modules/customerModule/customerModule";
import SnackbarMixin from "@/mixins/snackbarMixin";

@Component({
  name: "CreateCustomer",
  components: { BaseFormDialog, CompanyInfoField, Textfield },
})
export default class CreateCustomer extends Mixins(SnackbarMixin) {
  @Prop() editingCustomer?: ICustomerDTO;
  @Prop() defaultName?: string;
  @Prop() defaultPhone?: string;
  @Prop() defaultEmail?: string;

  public isSaving = false;
  public customerData: Partial<ICustomerDTO> = {
    firstName: "",
    lastName: "",
    phoneNumber: "",
    emailAddress: "",
    address: "",
    postalCode: "",
    city: "",
    insuranceId: 0,
    sendSmsReminder: false,
    sendEmailReminder: false,
    id: 0,
    insuranceName: "",
    phoneNumberCountryCode: "",
    gender: "",
    dateOfBirth: "",
    generalInfo: "",
    country: "",
    ssn: "",
    createdByCustomer: false,
  };

  // Validation rules
  public nameRules = [];

  created() {
    this.nameRules = [(v: string) => !!v || this.$t(this.$ts.validation.required).toString()];

    if (this.editingCustomer) {
      this.customerData = { ...this.editingCustomer };
    }

    // Apply defaults
    if (this.defaultName) this.customerData.firstName = this.defaultName;
    if (this.defaultPhone) this.customerData.phoneNumber = this.defaultPhone;
    if (this.defaultEmail) this.customerData.emailAddress = this.defaultEmail;
  }

  public validateField(fieldName: string) {
    const baseForm = this.$refs.baseForm as any;
    if (baseForm) {
      baseForm.validate();
    }
  }

  public async onSave() {
    const baseForm = this.$refs.baseForm as any;
    if (baseForm && baseForm.validate()) {
      this.isSaving = true;
      try {
        const updatedCustomer = await customersModule.putCustomer(this.customerData as ICustomerDTO); //TODO: We should really have a PostCustomerDTO and a PatchCustomerDTO and a PutCustomerDTO auto-generated by the backend
        this.$emit("created", updatedCustomer);
      } catch (e) {
        this.postSnackbarMessage(this.serverMessage(e));
      } finally {
        this.isSaving = false;
      }
    }
  }

  public async onCreate() {
    const baseForm = this.$refs.baseForm as any;
    if (baseForm && baseForm.validate()) {
      this.isSaving = true;
      try {
        const newCustomer = await customersModule.postCustomer(this.customerData);
        this.$emit("created", newCustomer);
      } catch (e) {
        this.postSnackbarMessage(this.serverMessage(e));
      } finally {
        this.isSaving = false;
      }
    }
  }

  /** The server's validation message ({ message, field }), or the error itself. */
  private serverMessage(e: any): string {
    return e?.response?.data?.message || e?.message || String(e);
  }

  public onCancel() {
    this.$emit("close");
  }
}
</script>

<template>
  <BaseFormDialog
    v-if="customerData != null"
    ref="baseForm"
    :title="customerData.id != null && customerData.id > 0 ? $t($ts.editCustomer) : $t($ts.createCustomer)"
    :is-editing="customerData.id != null && customerData.id > 0"
    :is-creating="customerData.id == 0 || customerData.id == null"
    :is-saving="isSaving"
    :save-text="$t($ts.buttons.save)"
    :create-text="$t($ts.buttons.create)"
    :cancel-text="$t($ts.buttons.cancel)"
    @cancel="onCancel"
    @save="onSave"
    @create="onCreate"
  >
    <!-- First Name -->
    <CompanyInfoField :label="$t($ts.name)">
      <Textfield
        v-model="customerData.firstName"
        :label="$t($ts.name)"
        :rules="nameRules"
        @input="validateField('firstName')"
      />
    </CompanyInfoField>

    <!-- Last Name -->
    <CompanyInfoField :label="$t($ts.lastName)">
      <Textfield
        v-model="customerData.lastName"
        :label="$t($ts.lastName)"
      />
    </CompanyInfoField>

    <v-divider />

    <!-- Phone Number -->
    <CompanyInfoField :label="$t($ts.phoneNumber)">
      <Textfield
        v-model="customerData.phoneNumber"
        :label="$t($ts.phoneNumber)"
        @input="validateField('phoneNumber')"
      />
    </CompanyInfoField>

    <!-- Email -->
    <CompanyInfoField :label="$t($ts.email)">
      <Textfield
        v-model="customerData.emailAddress"
        :label="$t($ts.email)"
        @input="validateField('emailAddress')"
      />
    </CompanyInfoField>

    <v-divider />

    <!-- Street Address -->
    <CompanyInfoField :label="$t($ts.address)">
      <Textfield
        v-model="customerData.address"
        :label="$t($ts.address)"
      />
    </CompanyInfoField>

    <!-- Postal Code -->
    <CompanyInfoField :label="$t($ts.postalCode)">
      <Textfield
        v-model="customerData.postalCode"
        :label="$t($ts.postalCode)"
      />
    </CompanyInfoField>

    <!-- City -->
    <CompanyInfoField :label="$t($ts.city)">
      <Textfield
        v-model="customerData.city"
        :label="$t($ts.city)"
      />
    </CompanyInfoField>

    <!-- Insurance -->
    <!-- <v-divider v-if="user.enablePaymentModule" />

    <CompanyInfoField v-if="user.enablePaymentModule" :label="$t($ts.insurance)">
      <v-select
        v-model="customerData.insuranceId"
        :label="$t($ts.selectInsurance)"
        :items="insurances"
        item-value="id"
        item-text="name"
      >
        <template v-slot:no-data>{{ $t($ts.noInsuranceData) }}</template>
      </v-select>
    </CompanyInfoField> -->
  </BaseFormDialog>
</template>

<style lang="scss" scoped>
@use "@/styles/global" as *;
</style>
